/**
 * @fileoverview Implementación de IAnalyticsRepository con Firestore y Cloud Functions
 * @module infrastructure/repositories/FirestoreAnalyticsRepository
 * @requires firebase/firestore
 * @requires firebase/functions
 */

import { 
  getFirestore, 
  collection, 
  doc, 
  getDoc, 
  query, 
  where,
  orderBy, 
  limit, 
  getDocs 
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import IAnalyticsRepository from '../../domain/ports/IAnalyticsRepository';
import AnalyticsReport from '../../domain/entities/AnalyticsReport';

/**
 * @class FirestoreAnalyticsRepository
 * @implements {IAnalyticsRepository}
 * @description Adaptador para analíticas utilizando Firestore y Cloud Functions
 * Implementa el contrato definido en IAnalyticsRepository
 */
class FirestoreAnalyticsRepository extends IAnalyticsRepository {
  /**
   * @constructor
   * @param {Object} logger - Sistema de logging para rastreo de eventos
   */
  constructor(logger) {
    super();
    this.db = getFirestore();
    this.functions = getFunctions();
    this.logger = logger;
    
    // Prefijos de colecciones para organización de datos
    this.collections = {
      reports: 'analytics_reports',
      dashboards: 'dashboards',
      userPreferences: 'user_preferences',
      trends: 'analytics_trends',
      kpis: 'analytics_kpis'
    };
    
    this.logger.info('[FirestoreAnalyticsRepository] Inicializado');
  }

  /**
   * @inheritdoc
   */
  async getReport(reportType, params) {
    this.logger.info(`[FirestoreAnalyticsRepository] Solicitando reporte ${reportType}`);
    
    try {
      // Primero verificamos si existe un reporte pre-calculado
      const cachedReport = await this._getCachedReport(reportType, params);
      if (cachedReport) {
        this.logger.info(`[FirestoreAnalyticsRepository] Usando reporte pre-calculado para ${reportType}`);
        return cachedReport;
      }
      
      // Si no existe, generamos el reporte mediante Cloud Function
      this.logger.info(`[FirestoreAnalyticsRepository] Generando reporte dinámico para ${reportType}`);
      const generateReport = httpsCallable(this.functions, 'generateAnalyticsReport');
      
      const result = await generateReport({
        reportType,
        params
      });
      
      // Validar la respuesta y transformarla a entidad de dominio
      if (!result.data || result.data.error) {
        throw new Error(result.data?.error || 'Error al generar reporte');
      }
      
      return result.data;
    } catch (error) {
      this.logger.error(`[FirestoreAnalyticsRepository] Error al obtener reporte: ${error.message}`, {
        reportType,
        error: error.message,
        stack: error.stack
      });
      
      throw new Error(`No se pudo generar el reporte: ${error.message}`);
    }
  }
  
  /**
   * Obtiene reporte pre-calculado de caché
   * @private
   * @param {string} reportType - Tipo de reporte
   * @param {Object} params - Parámetros de consulta
   * @returns {Promise<Object|null>} Reporte pre-calculado o null si no existe
   */
  async _getCachedReport(reportType, params) {
    try {
      // Construir consulta para buscar reportes pre-calculados que coincidan
      const reportsRef = collection(this.db, this.collections.reports);
      const q = query(
        reportsRef,
        where('type', '==', reportType),
        where('timeRange', '==', params.timeRange),
        // Más filtros según parámetros específicos
        orderBy('createdAt', 'desc'),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return null;
      }
      
      // Obtener y procesar el documento más reciente
      const reportDoc = snapshot.docs[0];
      const reportData = reportDoc.data();
      
      // Verificar frescura del reporte (máximo 1 hora)
      const createdAt = reportData.createdAt?.toDate() || new Date(0);
      const now = new Date();
      const maxAge = 60 * 60 * 1000; // 1 hora en ms
      
      if (now - createdAt > maxAge) {
        this.logger.debug(`[FirestoreAnalyticsRepository] Reporte encontrado pero obsoleto: ${reportDoc.id}`);
        return null;
      }
      
      this.logger.debug(`[FirestoreAnalyticsRepository] Reporte pre-calculado encontrado: ${reportDoc.id}`);
      return {
        id: reportDoc.id,
        ...reportData
      };
    } catch (error) {
      this.logger.warn(`[FirestoreAnalyticsRepository] Error al buscar reporte en caché: ${error.message}`);
      return null;
    }
  }
  
  /**
   * @inheritdoc
   */
  async getTrends(entity, params) {
    this.logger.info(`[FirestoreAnalyticsRepository] Obteniendo tendencias para ${entity}`);
    
    try {
      // Llamar a Cloud Function especializada
      const getTrends = httpsCallable(this.functions, 'getAnalyticsTrends');
      
      const result = await getTrends({
        entity,
        params
      });
      
      if (!result.data || result.data.error) {
        throw new Error(result.data?.error || 'Error al obtener tendencias');
      }
      
      return result.data.trends;
    } catch (error) {
      this.logger.error(`[FirestoreAnalyticsRepository] Error al obtener tendencias: ${error.message}`, {
        entity,
        error: error.message
      });
      
      throw new Error(`No se pudieron obtener las tendencias de ${entity}: ${error.message}`);
    }
  }
  
  /**
   * @inheritdoc
   */
  async getKpis(kpiNames, params) {
    this.logger.info(`[FirestoreAnalyticsRepository] Obteniendo KPIs [${kpiNames.join(', ')}]`);
    
    try {
      // Llamar a Cloud Function para calcular KPIs
      const getKpis = httpsCallable(this.functions, 'getKpis');
      
      const result = await getKpis({
        kpiNames,
        params
      });
      
      // Validar respuesta
      if (!result.data || result.data.error) {
        throw new Error(result.data?.error || 'Error al obtener KPIs');
      }
      
      return result.data.kpis;
    } catch (error) {
      this.logger.error(`[FirestoreAnalyticsRepository] Error al obtener KPIs: ${error.message}`, {
        kpiNames,
        error: error.message
      });
      
      throw new Error(`No se pudieron obtener los KPIs: ${error.message}`);
    }
  }
  
  /**
   * @inheritdoc
   */
  async detectAnomalies(metricName, params) {
    this.logger.info(`[FirestoreAnalyticsRepository] Detectando anomalías para ${metricName}`);
    
    try {
      // Llamar a Cloud Function especializada
      const detectAnomalies = httpsCallable(this.functions, 'detectAnomalies');
      
      const result = await detectAnomalies({
        metricName,
        params
      });
      
      // Validar respuesta
      if (!result.data || result.data.error) {
        throw new Error(result.data?.error || 'Error en detección de anomalías');
      }
      
      return result.data.anomalies;
    } catch (error) {
      this.logger.error(`[FirestoreAnalyticsRepository] Error al detectar anomalías: ${error.message}`, {
        metricName,
        error: error.message
      });
      
      throw new Error(`Detección de anomalías fallida: ${error.message}`);
    }
  }
  
  /**
   * @inheritdoc
   */
  async scheduleReport(reportConfig) {
    this.logger.info(`[FirestoreAnalyticsRepository] Programando reporte tipo ${reportConfig.type}`);
    
    try {
      // Llamar a Cloud Function para programar reporte
      const scheduleReport = httpsCallable(this.functions, 'scheduleReport');
      
      const result = await scheduleReport({
        reportConfig
      });
      
      // Validar respuesta
      if (!result.data || result.data.error) {
        throw new Error(result.data?.error || 'Error al programar reporte');
      }
      
      return result.data.reportId;
    } catch (error) {
      this.logger.error(`[FirestoreAnalyticsRepository] Error al programar reporte: ${error.message}`, {
        reportType: reportConfig.type,
        error: error.message
      });
      
      throw new Error(`No se pudo programar el reporte: ${error.message}`);
    }
  }
  
  /**
   * @inheritdoc
   */
  async getDashboard(dashboardId, params = {}) {
    this.logger.info(`[FirestoreAnalyticsRepository] Obteniendo dashboard ${dashboardId}`);
    
    try {
      // Primero intentamos obtener la configuración del dashboard
      const dashboardRef = doc(this.db, this.collections.dashboards, dashboardId);
      const dashboardSnapshot = await getDoc(dashboardRef);
      
      if (!dashboardSnapshot.exists()) {
        throw new Error(`Dashboard ${dashboardId} no encontrado`);
      }
      
      const dashboardConfig = dashboardSnapshot.data();
      
      // Llamar a Cloud Function para obtener datos actualizados para los widgets
      const getDashboardData = httpsCallable(this.functions, 'getDashboardData');
      
      const result = await getDashboardData({
        dashboardId,
        widgets: dashboardConfig.widgets,
        params
      });
      
      // Validar respuesta
      if (!result.data || result.data.error) {
        throw new Error(result.data?.error || 'Error al obtener datos del dashboard');
      }
      
      // Combinar configuración con datos actualizados
      return {
        id: dashboardId,
        title: dashboardConfig.title,
        description: dashboardConfig.description,
        layout: dashboardConfig.layout,
        widgets: result.data.widgets,
        metadata: {
          lastUpdated: new Date().toISOString(),
          ...result.data.metadata
        }
      };
    } catch (error) {
      this.logger.error(`[FirestoreAnalyticsRepository] Error al obtener dashboard: ${error.message}`, {
        dashboardId,
        error: error.message
      });
      
      throw new Error(`No se pudo cargar el dashboard: ${error.message}`);
    }
  }
  
  /**
   * @inheritdoc
   */
  async saveUserPreferences(userId, preferences) {
    this.logger.info(`[FirestoreAnalyticsRepository] Guardando preferencias para usuario ${userId}`);
    
    try {
      // Actualizar preferencias en Firestore
      const saveUserPref = httpsCallable(this.functions, 'saveUserPreferences');
      
      const result = await saveUserPref({
        userId,
        preferences
      });
      
      // Validar respuesta
      if (!result.data || result.data.error) {
        throw new Error(result.data?.error || 'Error al guardar preferencias');
      }
      
      return true;
    } catch (error) {
      this.logger.error(`[FirestoreAnalyticsRepository] Error al guardar preferencias: ${error.message}`, {
        userId,
        error: error.message
      });
      
      throw new Error(`No se pudieron guardar las preferencias: ${error.message}`);
    }
  }
  
  /**
   * @inheritdoc
   */
  async askNaturalLanguageQuestion(question, context = {}) {
    this.logger.info(`[FirestoreAnalyticsRepository] Procesando consulta natural: "${question.substring(0, 50)}..."`);
    
    try {
      // Llamar a Cloud Function con capacidades de ML para procesamiento
      const askQuestion = httpsCallable(this.functions, 'askAnalyticsQuestion');
      
      const result = await askQuestion({
        question,
        context
      });
      
      // Validar respuesta
      if (!result.data || result.data.error) {
        throw new Error(result.data?.error || 'No se pudo procesar la consulta');
      }
      
      return result.data;
    } catch (error) {
      this.logger.error(`[FirestoreAnalyticsRepository] Error al procesar consulta: ${error.message}`, {
        questionLength: question.length,
        error: error.message
      });
      
      throw new Error(`No pudimos responder tu pregunta: ${error.message}`);
    }
  }
}

export default FirestoreAnalyticsRepository;
